import React, { useState, useEffect } from 'react';
import {
    exportModerationPackage,
    importModerationPackage,
    getModerationHistory
} from '../api/patModeration';

const PATModerationSimplified = ({ token, patAssessment, learners }) => {
    const [selectedLearners, setSelectedLearners] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [moderationHistory, setModerationHistory] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showInstructions, setShowInstructions] = useState(false);

    useEffect(() => {
        if (patAssessment) {
            loadModerationHistory();
        }
    }, [patAssessment]);

    const loadModerationHistory = async () => {
        try {
            const data = await getModerationHistory(token, patAssessment.id);
            setModerationHistory(data);
        } catch (err) {
            console.error('Error loading moderation history:', err);
        }
    };

    const handleSelectAll = () => {
        if (selectedLearners.length === learners.length) {
            setSelectedLearners([]);
        } else {
            setSelectedLearners(learners.map(l => l.id));
        }
    };

    const handleSelectLearner = (learnerId) => {
        if (selectedLearners.includes(learnerId)) {
            setSelectedLearners(selectedLearners.filter(id => id !== learnerId));
        } else {
            setSelectedLearners([...selectedLearners, learnerId]);
        }
    };

    const handleExport = async () => {
        setError('');
        setSuccess('');
        
        if (selectedLearners.length === 0) {
            setError('Please select at least one learner to export');
            return;
        }

        setExporting(true);
        try {
            // First regenerate reports to ensure latest data
            if (window.confirm(`This will regenerate PAT reports for ${selectedLearners.length} learners to ensure the latest marking data is included. Continue?`)) {
                const updatePromises = selectedLearners.map(learnerId => 
                    fetch(`/api/pat/update-report-by-assessment/`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Token ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            pat_assessment_id: patAssessment.id,
                            learner_id: learnerId,
                            deploy_to_github: true
                        })
                    })
                );
                
                await Promise.all(updatePromises);
            }

            // Export moderation package
            const result = await exportModerationPackage(token, {
                pat_assessment_id: patAssessment.id,
                selected_learner_ids: selectedLearners,
                repository_name: '1058-2025'
            });

            if (result.success) {
                setSuccess(`Successfully exported moderation package for ${result.learner_count} learners`);
                
                // Download the file
                const downloadUrl = `/api/pat-moderation/download/${result.file_name}/`;
                const response = await fetch(downloadUrl, {
                    headers: {
                        'Authorization': `Token ${token}`,
                    },
                });
                
                if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = result.file_name;
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                    
                    setShowInstructions(true);
                } else {
                    setError('Failed to download the moderation package');
                }
                
                loadModerationHistory();
            }
        } catch (err) {
            setError(`Export failed: ${err.message}`);
        } finally {
            setExporting(false);
        }
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setError('');
        setSuccess('');
        setImporting(true);
        setShowInstructions(false);

        try {
            const result = await importModerationPackage(token, file, '1058-2025');

            if (result.success) {
                setSuccess(
                    `Successfully imported moderation: ${result.updated_learners} learners updated with ${result.total_changes} changes`
                );
                
                loadModerationHistory();
                event.target.value = '';
            }
        } catch (err) {
            setError(`Import failed: ${err.message}`);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">PAT Moderation Management</h3>
            
            {/* Simple explanation */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
                <h4 className="font-medium text-blue-900 mb-2">Simple 3-Step Process:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                    <li>Export a moderation package (ZIP file) with selected learners</li>
                    <li>Open the package, review and modify scores offline</li>
                    <li>Import the modified package back into Caliper</li>
                </ol>
            </div>

            {/* Error/Success Messages */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                    {error}
                </div>
            )}
            {success && (
                <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
                    {success}
                </div>
            )}

            {/* Step 1: Export */}
            <div className="mb-8 p-4 border rounded">
                <h4 className="text-lg font-medium mb-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-500 text-white rounded-full mr-2">1</span>
                    Export for Moderation
                </h4>
                
                <div className="ml-10">
                    <div className="mb-4">
                        <div className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                checked={selectedLearners.length === learners.length}
                                onChange={handleSelectAll}
                                className="mr-2"
                            />
                            <label className="font-medium">Select All Learners ({learners.length})</label>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded p-2">
                            {learners.map(learner => (
                                <label key={learner.id} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={selectedLearners.includes(learner.id)}
                                        onChange={() => handleSelectLearner(learner.id)}
                                        className="mr-2"
                                    />
                                    <span className="text-sm">{learner.full_name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    
                    <button
                        onClick={handleExport}
                        disabled={exporting || selectedLearners.length === 0}
                        className={`px-6 py-2 rounded font-medium ${
                            exporting || selectedLearners.length === 0
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                        {exporting ? (
                            <>
                                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                                Exporting...
                            </>
                        ) : (
                            '📦 Export Moderation Package'
                        )}
                    </button>
                </div>
            </div>

            {/* Step 2: Instructions */}
            {showInstructions && (
                <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded">
                    <h4 className="text-lg font-medium mb-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-yellow-500 text-white rounded-full mr-2">2</span>
                        Moderation Instructions
                    </h4>
                    
                    <div className="ml-10 space-y-2 text-sm">
                        <p><strong>Your package has been downloaded!</strong> Now:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-4">
                            <li>Extract the ZIP file to a folder on your computer</li>
                            <li>Open the <code className="bg-gray-100 px-1">index.html</code> file in your web browser</li>
                            <li>Click on each learner to review their assessment</li>
                            <li>Modify scores by clicking on them and entering new values</li>
                            <li>Add moderation notes for each change</li>
                            <li>Click "Save Changes" button for each learner</li>
                            <li className="font-bold text-red-600">When done with ALL learners, click "Save All Changes to Files" button on the main page</li>
                            <li className="font-bold">Follow the instructions to update the JSON files with your changes</li>
                            <li>ZIP the entire folder (with updated files)</li>
                            <li>Import the ZIP file back using the import section below</li>
                        </ol>
                        
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                            <p className="text-red-700 font-medium">⚠️ Important: Your changes are stored in the browser until you click "Save All Changes to Files"</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3: Import */}
            <div className="mb-8 p-4 border rounded">
                <h4 className="text-lg font-medium mb-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-green-500 text-white rounded-full mr-2">3</span>
                    Import Moderated Package
                </h4>
                
                <div className="ml-10">
                    <p className="text-sm text-gray-600 mb-3">
                        After completing moderation, ZIP the entire folder and import it here.
                    </p>
                    
                    <input
                        type="file"
                        accept=".zip"
                        onChange={handleImport}
                        disabled={importing}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                        id="import-file"
                    />
                    
                    {importing && (
                        <p className="mt-2 text-sm text-gray-600">
                            <span className="inline-block w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin mr-2"></span>
                            Importing changes...
                        </p>
                    )}
                </div>
            </div>

            {/* History */}
            <div className="border-t pt-6">
                <h4 className="text-lg font-medium mb-3">Moderation History</h4>
                
                {moderationHistory.length === 0 ? (
                    <p className="text-gray-500">No moderation history available</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-2">Date</th>
                                    <th className="text-left py-2">Status</th>
                                    <th className="text-left py-2">Learners</th>
                                    <th className="text-left py-2">Moderator</th>
                                    <th className="text-left py-2">Changes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {moderationHistory.map(history => (
                                    <tr key={history.id} className="border-b">
                                        <td className="py-2">
                                            {new Date(history.export_timestamp).toLocaleDateString()}
                                        </td>
                                        <td className="py-2">
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                history.status === 'imported' 
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {history.status}
                                            </span>
                                        </td>
                                        <td className="py-2">{history.learner_count}</td>
                                        <td className="py-2">{history.moderator_name || '-'}</td>
                                        <td className="py-2">{history.changes_count || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PATModerationSimplified;